import { Card, CardContent, TextField, Typography } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { AppState } from '../../../state/configureStore';
import {
  clearProductType,
  createProductType,
  fetchProductType,
  ProductType,
  ProductTypeState,
  updateProductType,
} from '../../../state/productType';
import { FixedPositionButtons } from '../../components';
import useStyles from './productTypeForm.style';

const ProductTypeSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
});

interface ProductTypeFormRouteParamsProps {
  id: string;
}

interface ProductTypeFormProps
  extends RouteComponentProps<ProductTypeFormRouteParamsProps> {}

const ProductTypeForm: React.FC<ProductTypeFormProps> = props => {
  const { match, history } = props;
  const {
    params: { id: paramsId },
  } = match;
  const classes = useStyles();
  const {
    item: { name = '', id = 0, description = '' },
  } = useSelector<AppState, ProductTypeState>(state => state.productType);
  const dispatch = useDispatch();

  const redirectToProductTypeList = () => {
    history.push('/settings/productTypes');
  };

  const handleSubmit = (values: ProductType, actions: FormikHelpers<any>) => {
    if (paramsId) {
      dispatch(updateProductType({ values, history }));
    } else {
      dispatch(createProductType({ values, history }));
    }
    actions.setSubmitting(false);
  };

  useEffect(() => {
    if (paramsId) {
      dispatch(fetchProductType(+paramsId));
    } else {
      dispatch(clearProductType());
    }

    return function cleanUp() {
      dispatch(clearProductType());
    };
  }, [paramsId, dispatch]);

  return (
    <Card className={classes.root}>
      <Formik
        enableReinitialize={true}
        initialValues={{ name, id, description }}
        validationSchema={ProductTypeSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, errors, touched, submitForm }) => {
          return (
            <Form>
              <Typography variant="h4" className={classes.title}>
                {paramsId === undefined ? `New ProductType` : `Update ProductType`}
              </Typography>
              <CardContent>
                <TextField
                  name="name"
                  type="input"
                  label="Name"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={values.name}
                  onChange={handleChange}
                  error={!!errors.name && !!touched.name}
                  helperText={errors.name}
                />
                <TextField
                  name="description"
                  type="input"
                  label="Description"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={values.description}
                  onChange={handleChange}
                  error={!!errors.description && !!touched.description}
                  helperText={errors.description}
                />
              </CardContent>
              <FixedPositionButtons
                actions={[
                  {
                    name: paramsId === undefined ? `Create` : `Update`,
                    onClick: () => {
                      submitForm();
                    },
                    color: 'primary' as 'primary',
                    disabled: false,
                    icon: 'save',
                  },
                  {
                    name: 'CANCEL',
                    onClick: redirectToProductTypeList,
                    color: 'default' as 'default',
                    disabled: false,
                  },
                ]}
                open
              />
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};

export default ProductTypeForm;
